/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Merchant-defined (`c_*`) product attributes and the rule each one uses to answer *"whose value is
 * this — the variant's, or its master's?"*. See `README.md` for how to add one.
 *
 * One policy is in use today: always-master. The catalogue populates every one of these attributes on
 * variant `00.4118.474.190` as well as on its master, so a variant-first read of any of them would
 * appear to work. The policy follows from what an attribute MEANS, not from what the data currently
 * allows. `README.md` keeps the other two policies documented as taxonomy — variant-first has no
 * current consumer, direct-read is unbuilt.
 *
 * Shared shape: readers are pure, sync, and return `undefined`; resolvers take the master promise
 * the caller already has in flight (so no branch costs an extra SCAPI call), always return a
 * promise, and return `null` for "resolved, but empty". The duplication between readers is
 * deliberate — a shared helper would couple attributes whose policies are meant to diverge.
 */
import type { ShopperProducts, ShopperSearch } from '@/scapi';

/**
 * Either SCAPI shape a `c_*` attribute can arrive on. Both reach TypeScript through their schema's
 * `[key: string]: unknown` index signature, so the readers are shape-agnostic. The PDP passes a
 * `Product`; the PLP passes a `ProductSearchHit`.
 */
type MaybeProduct = ShopperProducts.schemas['Product'] | ShopperSearch.schemas['ProductSearchHit'] | null | undefined;
type MasterPromise = Promise<ShopperProducts.schemas['Product'] | null | undefined> | null | undefined;

// ─────────────────────────────────────────────────────────────────────────────
// Policy 1 — always-master
//
// The master's value is the answer, always; a variant's own value is ignored even when it has one,
// because these attributes identify the MODEL rather than the item. A value that differed per
// variant would be a data error, not a variation.
//
// Fallbacks differ between the three below, because the cost of an empty result does: an absent
// model code renders as nothing and an absent Features block renders as nothing, while an absent
// heading is an SEO and a11y defect.
//
// ON A SEARCH HIT these attributes are not at the top level, which is why the PLP needs the
// `readHit*` variants below. Verified against the running site (2026-08-13): a `ProductSearchHit`
// carries neither attribute on itself, even with `expand=custom_properties` requested — both arrive
// on `representedProduct`.
//
// That makes the PLP a variant-level read serving a master-level policy, sound only because the
// catalogue mirrors model identity onto every variant: master `SRAM-TEST` renders `SRAM-TEST-1234` /
// "Deluxe Ultimate" on its PDP, and its represented variant carries those same two strings. The
// top-level tier is kept FIRST so a site that does index them at hit level gets the true master
// value with no change here.
//
// There is no third tier. Recovering a value the hit does not carry costs one master fetch PER TILE.
// If the mirroring stops holding, the fix belongs in the search index, not in the tile.
// ─────────────────────────────────────────────────────────────────────────────

/** Master-level model code — the identifier line under the PDP heading. */
export const MODEL_CODE_ATTRIBUTE = 'c_model-code';

/**
 * Master-level display name — the PDP `<h1>`.
 *
 * Verified against the SRAM sandbox: master `MC-SDLX-ULT` carries `"Super Deluxe Ultimate"` here
 * while its typed `name` is `"Super Deluxe Ultimate Rear Shock"`. The two are genuinely different
 * strings, which is the point of preferring this one.
 */
export const MODEL_NAME_FOR_WEB_ATTRIBUTE = 'c_model-name-for-web';

/**
 * Reads `c_model-code`, narrowing the untyped custom attribute to a string.
 *
 * @param product - The product to read from; `null`/`undefined` yields `undefined`
 * @returns The trimmed model code, or `undefined` when unset, blank, or not a string
 */
export function readModelCode(product: MaybeProduct): string | undefined {
    const value = product?.[MODEL_CODE_ATTRIBUTE];

    if (typeof value !== 'string') return undefined;

    const trimmed = value.trim();

    return trimmed === '' ? undefined : trimmed;
}

/**
 * Reads `c_model-name-for-web`, narrowing the untyped custom attribute to a string.
 *
 * @param product - The product to read from; `null`/`undefined` yields `undefined`
 * @returns The trimmed model name, or `undefined` when unset, blank, or not a string
 */
export function readModelNameForWeb(product: MaybeProduct): string | undefined {
    const value = product?.[MODEL_NAME_FOR_WEB_ATTRIBUTE];

    if (typeof value !== 'string') return undefined;

    const trimmed = value.trim();

    return trimmed === '' ? undefined : trimmed;
}

/**
 * The represented product carried by a search hit, WHEN it is a variant of that hit.
 *
 * SCAPI types it as `{ id: string }` but populates it with that product's `c_*` attributes — the
 * same undeclared shape `product-badges.ts` reads `c_isSale` from.
 *
 * Bundles and sets yield nothing: for them `representedProduct` is a MEMBER, a separate product with
 * its own model identity, so reading it would label a set with one member's code and name. The PLP
 * renderers refuse to build a `?pid=` from it on the same grounds.
 */
function representedProductOf(hit: MaybeProduct): MaybeProduct {
    const productType = (hit as { productType?: { bundle?: boolean; set?: boolean } } | null | undefined)?.productType;

    if (productType?.bundle || productType?.set) return undefined;

    // Cast through `unknown`: a partial product satisfies none of either schema's required fields,
    // but the readers touch it only through the index signature both shapes have.
    return (hit as { representedProduct?: unknown } | null | undefined)?.representedProduct as MaybeProduct;
}

/**
 * Reads `c_model-code` from a search hit: hit top level, then `representedProduct`.
 *
 * @param hit - The search hit to read from; `null`/`undefined` yields `undefined`
 * @returns The trimmed model code, or `undefined` when neither tier carries one
 */
export function readHitModelCode(hit: MaybeProduct): string | undefined {
    return readModelCode(hit) ?? readModelCode(representedProductOf(hit));
}

/**
 * Reads `c_model-name-for-web` from a search hit: hit top level, then `representedProduct`.
 *
 * @param hit - The search hit to read from; `null`/`undefined` yields `undefined`
 * @returns The trimmed model name, or `undefined` when neither tier carries one
 */
export function readHitModelNameForWeb(hit: MaybeProduct): string | undefined {
    return readModelNameForWeb(hit) ?? readModelNameForWeb(representedProductOf(hit));
}

/**
 * Resolves the PDP identifier line: master attribute → master id → `null`.
 *
 * The master `id` is a sound fallback because master `c_model-code` matches the master id by
 * convention (`MC-SDLX-ULT`), so an unset attribute still yields the right string. Reads the
 * FETCHED master, never `product.master` — SCAPI's `Master` sub-object carries ids and prices only,
 * no custom attributes.
 *
 * @param product - The resolved product (variant, master, or standalone)
 * @param masterProductPromise - The in-flight master fetch, or `null` when there is no master
 * @param onError - Notified when the master fetch rejects, for request-scoped logging
 * @returns The master-level model code, or `null` when unresolvable
 */
export function resolveModelCode(
    product: ShopperProducts.schemas['Product'],
    masterProductPromise: MasterPromise,
    onError?: (error: unknown) => void
): Promise<string | null> {
    if (product.type?.variant && masterProductPromise) {
        return Promise.resolve(masterProductPromise)
            .then((master) => readModelCode(master) ?? master?.id ?? null)
            .catch((error) => {
                onError?.(error);

                return null;
            });
    }

    // A master resolved directly, or a standalone (`type.item`) with no master at all — either way
    // its own attribute IS the master-level value. A variant whose master fetch was never started
    // lands here too.
    return Promise.resolve(readModelCode(product) ?? product.id ?? null);
}

/**
 * Resolves the PDP heading: master attribute → master `name` → `null`, which the caller reads as
 * "keep `product.name`".
 *
 * Degrades to the typed `name` rather than to `null` whenever a name exists — an empty `<h1>` is an
 * SEO and a11y defect, unlike the model-code line, which can simply be absent.
 *
 * @param product - The resolved product (variant, master, or standalone)
 * @param masterProductPromise - The in-flight master fetch, or `null` when there is no master
 * @returns The master-level name, or `null` when unresolvable
 */
export function resolveModelName(
    product: ShopperProducts.schemas['Product'],
    masterProductPromise: MasterPromise
): Promise<string | null> {
    if (product.type?.variant && masterProductPromise) {
        return Promise.resolve(masterProductPromise)
            .then((master) => readModelNameForWeb(master) ?? master?.name ?? null)
            .catch(() => null);
    }

    return Promise.resolve(readModelNameForWeb(product) ?? product.name ?? null);
}

/**
 * Features copy for the B2B channel — the block in the PDP's Details panel (CRCB2B-313).
 *
 * The D2C counterpart (`c_featurespublic`) is a SEPARATE field holding different copy for a
 * different audience, never a fallback for this one. It is not declared here because no D2C surface
 * exists to read it; add it beside this constant when one does.
 */
export const FEATURES_B2B_ATTRIBUTE = 'c_featuresam';

/**
 * Reads the Features copy, narrowing the untyped custom attribute to a string.
 *
 * `trim()` only touches the ends, so INTERNAL newlines survive — the rendered block relies on them
 * for its line breaks.
 *
 * @param product - The product to read from; `null`/`undefined` yields `undefined`
 * @returns The end-trimmed copy, or `undefined` when unset, blank, or not a string
 */
export function readFeatures(product: MaybeProduct): string | undefined {
    const value = product?.[FEATURES_B2B_ATTRIBUTE];

    if (typeof value !== 'string') return undefined;

    const trimmed = value.trim();

    return trimmed === '' ? undefined : trimmed;
}

/**
 * Resolves the Features copy under the always-master policy.
 *
 * A variant's own copy is IGNORED even when populated, exactly as for the model code and model name:
 * this copy describes the model, so a variant carrying different text would be a data error rather
 * than a variation.
 *
 * The attribute previously resolved variant-first. Correcting it does not change any rendered output
 * on the current catalogue — the master and its variants carry identical text — which is precisely
 * why the policy has to be stated rather than left to pass by coincidence.
 *
 * A rejected master resolves to `null` rather than propagating: the block is decorative copy, and
 * `fetchProductById` has already logged the SCAPI failure at the API layer.
 *
 * @param product - The resolved product (variant, master, or standalone)
 * @param masterProductPromise - The in-flight master fetch, or `null` when there is no master
 * @returns The resolved copy, or `null` when unresolvable
 */
export function resolveFeatures(
    product: ShopperProducts.schemas['Product'],
    masterProductPromise: MasterPromise
): Promise<string | null> {
    if (product.type?.variant && masterProductPromise) {
        return Promise.resolve(masterProductPromise)
            .then((master) => readFeatures(master) ?? null)
            .catch(() => null);
    }

    // A master resolved directly, or a standalone with no master at all — its own attribute IS the
    // master-level value. A variant whose master fetch was never started lands here too.
    return Promise.resolve(readFeatures(product) ?? null);
}
